import useSubstratePanelStore from '../../stores/substratePanelStore';

/**
 * GoldenRatioContainer - Reusable page container with golden ratio layout
 *
 * Golden ratio: 1.618
 * If page width = x, golden sections are ~61.8% (y) and ~38.2% (z)
 * Content takes y (61.8%), margins take z/2 (19.1%) on each side
 * This creates: 19.1% margin | 61.8% content | 19.1% margin
 *
 * Behaviour:
 * - Fullscreen (no substrate panel open): golden ratio centring.
 * - Panel open: flat container — the panel/rail already eats ~470px on the
 *   right; double-counting starves cards.
 *
 * Usage:
 * <GoldenRatioContainer>
 *   <div>Your header</div>
 *   <GoldenRatioContainer.ContentSection withBackground>
 *     <div>Your cards</div>
 *   </GoldenRatioContainer.ContentSection>
 * </GoldenRatioContainer>
 */
export default function GoldenRatioContainer({ children, className = '' }) {
  const activePanel = useSubstratePanelStore((s) => s.activePanel);
  const fullscreen = activePanel == null;
  const inner = fullscreen ? 'mx-2 sm:mx-8 lg:mx-[19.1%] min-w-0' : 'min-w-0';
  // The in-page People workspace already receives the phone/tablet gutter.
  // Keep other panels' existing spacing unchanged.
  const outer = fullscreen
    ? 'container mx-auto mt-2 sm:mt-4'
    : activePanel === 'people'
      ? 'container mx-auto mt-2 sm:mt-4 px-0 lg:px-4'
      : 'container mx-auto mt-2 sm:mt-4 px-4 sm:px-6';
  return (
    <div className={outer}>
      <div className={`${inner} ${className}`}>
        {children}
      </div>
    </div>
  );
}

/**
 * Content section with optional background for card containers
 */
GoldenRatioContainer.ContentSection = function ContentSection({
  children,
  withBackground = false,
  className = ''
}) {
  const backgroundClass = withBackground
    ? 'bg-base-300 rounded-2xl p-4 sm:p-6 shadow-inner border border-base-300'
    : '';

  return (
    <div className={`${backgroundClass} ${className} overflow-x-hidden`}>
      {children}
    </div>
  );
};
